import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { SubscriptionPanel, InviteForm, InviteList } from "@/components/billing/BillingParts";

const errMsg = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return typeof detail === "string" ? detail : (detail?.message || fallback);
};

function useInvites() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/invites");
      setInvites(data);
    } catch (e) {
      console.error("Failed to load invites", e);
      toast.error("Failed to load invites. Please try again.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { invites, setInvites, loading };
}

function usePortal() {
  const [openingPortal, setOpeningPortal] = useState(false);
  const [openingCancel, setOpeningCancel] = useState(false);

  const go = async (setBusy, extra, fallback) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/settings/billing`, ...extra,
      });
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (e) {
      toast.error(errMsg(e, fallback));
    } finally { setBusy(false); }
  };

  const onPortal = () => go(setOpeningPortal, {}, "Portal unavailable");
  const onCancel = () => {
    if (!window.confirm(
      "Cancel your Gridiron OS subscription?\n\n" +
      "You'll be taken to Stripe's secure cancellation screen. Your plan stays active until the end of the current billing period."
    )) return;
    go(setOpeningCancel, { flow: "cancel" }, "Cancel unavailable");
  };
  return { onPortal, onCancel, openingPortal, openingCancel };
}

export default function BillingSettings() {
  const { status } = useBilling();
  const { invites, setInvites, loading } = useInvites();
  const portal = usePortal();
  const [form, setForm] = useState({ email: "", role: "assistant_coach" });
  const [sending, setSending] = useState(false);

  const invite = async () => {
    if (!form.email) return toast.error("Email required");
    setSending(true);
    try {
      const { data } = await api.post("/billing/invites", form);
      setInvites(prev => [data, ...prev]);
      setForm({ email: "", role: "assistant_coach" });
      toast.success("Invite created — share the link below with the coach");
    } catch (e) {
      toast.error(errMsg(e, "Invite failed"));
    } finally { setSending(false); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try {
      await api.delete(`/billing/invites/${inv.id}`);
      setInvites(prev => prev.map(x => x.id === inv.id ? { ...x, status: "revoked" } : x));
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(errMsg(e, "Could not revoke invite"));
    }
  };

  const copyInviteLink = (inv) => {
    navigator.clipboard.writeText(`${window.location.origin}/register?invite=${inv.token}`);
    toast.success("Invite link copied");
  };

  const plan = status?.effective_plan;
  const canInvite = plan && plan !== "free";

  return (
    <div data-testid="billing-settings-page">
      <TopBar title="Settings — Billing" subtitle="Plan, seats, and coach invites"
        actions={
          <Link to="/pricing" data-testid="billing-view-plans"
            className="text-[10px] font-condensed uppercase tracking-[0.28em] text-amber hover:underline">
            View plans →
          </Link>
        }
      />
      <div className="p-8 max-w-5xl space-y-6">
        <SubscriptionPanel status={status} {...portal} />
        <div className="bg-surface border border-surface-3">
          <div className="p-6 border-b border-surface-3 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber" />
                <div className="font-condensed uppercase tracking-[0.25em] text-sm">Coach Seats</div>
              </div>
              <div className="text-sm text-slate-400 mt-2">
                {plan === "school" ? "School Plan — up to 15 coaches on your program library." :
                  plan === "pro" ? "Pro Coach — 3 seats. Upgrade to School Plan for a full staff." :
                  "Invites are locked on the Free plan. Upgrade to invite coaches."}
              </div>
            </div>
          </div>
          {canInvite && (
            <InviteForm form={form} setForm={setForm} onSubmit={invite} sending={sending}
              programLabel={status?.meta?.label} />
          )}
          <InviteList invites={invites} loading={loading} onCopy={copyInviteLink} onRevoke={revoke} />
        </div>
      </div>
    </div>
  );
}
